import { useState, useCallback, useRef } from 'react';
import { initialFormData } from '../constants/initialStates';
import { useLanguage } from '../../../../contexts/LanguageContext';

const useDeclarationForm = () => {
    const { t } = useLanguage();

    const prevRecordIdRef = useRef(null);

    const [formData, setFormData] = useState({
        ...initialFormData,
        lastKmInfo: 0,
        warrantyBeginDate: null,
        warrantyEndDate: null,
        chassisDescription: '',
        registrationDate: null
    });

    const [errors, setErrors] = useState({});
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [filters, setFilters] = useState({ status: 5 });

    const [chassisData, setChassisData] = useState(null);
    const [showChassisDialog, setShowChassisDialog] = useState(false);

    const [updatedFiles, setUpdatedFiles] = useState([]);

    const resetAllStates = useCallback(() => {
        setFormData({
            ...initialFormData,
            lastKmInfo: 0,
            warrantyBeginDate: null,
            warrantyEndDate: null,
            chassisDescription: '',
            registrationDate: null,
            deliveryPhone: '',
            balanceInfo: '',
            serviceNote: ''
        });
        setErrors({});
        setChassisData(null);
        setUpdatedFiles([]);
        setShowChassisDialog(false);
        prevRecordIdRef.current = null;
    }, []);

    const updateField = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    }, [errors]);

    const validateForm = () => {
        const newErrors = {};

        const requiredFields = {
            serviceDeliveryDate: t('warranty.customerDeclaration.validation.serviceDeliveryDateRequired'),
            chassisNumber: t('warranty.customerDeclaration.validation.chassisNumberRequired'),
            customer: t('warranty.customerDeclaration.validation.customerIdRequired'),
            customerPhone: t('warranty.customerDeclaration.validation.customerPhoneRequired'),
            customerStatement: t('warranty.customerDeclaration.validation.declarationRequired'),
            receivingPerson: t('warranty.customerDeclaration.validation.receivedByRequired'),
            receivingDeliveryPerson: t('warranty.customerDeclaration.validation.deliveredByRequired')
        };

        Object.entries(requiredFields).forEach(([key, message]) => {
            const value = formData[key];
            if (!value || (typeof value === 'string' && !value.trim()) || value === 0) {
                newErrors[key] = message;
            }
        });

        if (!formData.kmInformation || Number(formData.kmInformation) <= 0) {
            newErrors.kmInformation = t('warranty.customerDeclaration.validation.kilometerPositiveRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEdit = useCallback((rowData) => {
        if (prevRecordIdRef.current !== rowData.id) {
            resetAllStates();
        }

        prevRecordIdRef.current = rowData.id;

        setFormData({
            ...initialFormData,
            ...rowData,
            id: rowData.id,
            serviceDeliveryDate: rowData.vehicleAcceptDate || null,
            chassisNumber: rowData.chassisNo || '',
            customer: rowData.customer || rowData.customerId || 0,
            customerName: rowData.customerNameSurname || rowData.customerName || '',
            customerNameSurname: rowData.customerNameSurname || '',
            customerPhone: rowData.customerPhone || '',
            plateNumber: rowData.licensePlate || '',
            kmInformation: rowData.kmInformation || rowData.kilometer || 0,
            receivingPerson: rowData.receivingPerson || rowData.receivedBy || '',
            receivingDeliveryPerson: rowData.receivingDeliveryPerson || rowData.deliveredBy || '',
            deliveryPhone: rowData.deliveryPhone || '',
            customerStatement: rowData.customerStatement || rowData.declaration || '',
            lastKmInfo: 0,
            warrantyBeginDate: null,
            warrantyEndDate: null,
            registrationDate: null,
            chassisDescription: '',
            isDomestic: rowData.isDomestic ?? false,
            balanceInfo: rowData.balanceInfo || rowData.bakiyeBilgisi || '',
            serviceNote: rowData.serviceNote || ''
        });

        setErrors({});
        setEditMode(true);
        setChassisData(null);
        setUpdatedFiles([]);
        setDialogVisible(true);
    }, [resetAllStates]);

    const handleNew = useCallback(() => {
        resetAllStates();

        setFormData({
            ...initialFormData,
            serviceDeliveryDate: new Date().toISOString(),
            lastKmInfo: 0,
            kmInformation: 0,
            receivingPerson: '',
            receivingDeliveryPerson: '',
            deliveryPhone: '',
            customerStatement: '',
            plateNumber: '',
            customerName: '',
            customerPhone: '',
            warrantyBeginDate: null,
            warrantyEndDate: null,
            chassisDescription: '',
            balanceInfo: '',
            serviceNote: ''
        });

        setErrors({});
        setChassisData(null);
        setEditMode(false);
        setUpdatedFiles([]);
        setDialogVisible(true);
    }, [resetAllStates]);

    const handleSave = () => {
        if (!validateForm()) return null;

        const payload = {
            ...formData,
            customerId: Number(formData.customer) || 0,
            customerPhone: String(formData.customerPhone || ''),
            kilometer: Number(formData.kmInformation) || 0,
            kmInformation: Number(formData.kmInformation) || 0,
            declaration: formData.customerStatement || '',
            customerStatement: formData.customerStatement || '',
            receivedBy: formData.receivingPerson || '',
            receivingPerson: formData.receivingPerson || '',
            deliveredBy: formData.receivingDeliveryPerson || '',
            receivingDeliveryPerson: formData.receivingDeliveryPerson || '',
            deliveryPhone: formData.deliveryPhone || '',
            brand: formData.brand || '',
            model: formData.model || '',
            plateNumber: formData.plateNumber || '',
            chassisNumber: formData.chassisNumber || '',
            serviceDeliveryDate: formData.serviceDeliveryDate || new Date().toISOString(),
            registrationDate: formData.registrationDate || null,
            warrantyBeginDate: formData.warrantyBeginDate || null,
            warrantyEndDate: formData.warrantyEndDate || null,
            bosBranchCode: formData.bosBranchCode || '',
            bosBranchSiteCode: formData.bosBranchSiteCode || '',
            serviceNote: formData.serviceNote || ''
        };

        return payload;
    };

    const handleClose = useCallback(() => {
        setDialogVisible(false);

        resetAllStates();
        setEditMode(false);
    }, [resetAllStates]);

    const handleDelete = () => formData.id;

    const handleApplyFilters = (updatedFilters) => {
        const cleanedFilters = Object.fromEntries(
            Object.entries(updatedFilters).filter(([_, v]) => v != null && v !== '')
        );
        setFilters(cleanedFilters);
    };

    const handleResetFilters = () => setFilters({ status: 5 });

    const onFilesChange = useCallback((files) => {
        setUpdatedFiles(files);
    }, []);

    const onRemoveFile = useCallback((indexToRemove) => {
        setUpdatedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    }, []);

    return {
        formData,
        setFormData,
        updateField,
        errors,
        dialogVisible,
        editMode,
        filters,
        chassisData,
        setChassisData,
        showChassisDialog,
        setShowChassisDialog,
        handleNew,
        handleEdit,
        handleSave,
        handleDelete,
        handleClose,
        handleApplyFilters,
        handleResetFilters,
        updatedFiles,
        onFilesChange,
        onRemoveFile,
        resetAllStates
    };
};

export default useDeclarationForm;